import { Badge, type BadgeVariantProps } from '@azelis/ui/components/badge';
import { Button } from '@azelis/ui/components/button';
import { Card, CardContent } from '@azelis/ui/components/card';
import { PageSection } from '@azelis/ui/components/pageSection';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@azelis/ui/components/table';
import { Link } from '@tanstack/react-router';

import { useDeleteQuestionnaire, useQuestionnaires } from '@/api/questionnaires';
import type { SubmissionStatus } from '@/api/types';

const STATUS_BADGE_VARIANT: Record<SubmissionStatus, NonNullable<BadgeVariantProps['variant']>> = {
  DONE: 'success',
  FAILED: 'destructive',
  PROCESSING: 'info',
};

export default function Overview() {
  const { data: questionnaires, isLoading } = useQuestionnaires();
  const deleteQuestionnaire = useDeleteQuestionnaire();

  const rows = questionnaires ?? [];

  return (
    <PageSection>
      <Card className="w-full">
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((questionnaire) => (
                  <TableRow key={questionnaire.id}>
                    <TableCell>{questionnaire.title}</TableCell>
                    <TableCell>{new Date(questionnaire.createdOn).toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_BADGE_VARIANT[questionnaire.status]}>
                        {questionnaire.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-3">
                        {questionnaire.status === 'DONE' && (
                          <Button
                            variant="link"
                            size="sm"
                            render={
                              <Link
                                to="/app/overview/$id"
                                params={{ id: questionnaire.id }}
                              />
                            }
                          >
                            View
                          </Button>
                        )}
                        <Button
                          variant="link"
                          size="sm"
                          className="text-destructive-foreground"
                          onClick={() => {
                            deleteQuestionnaire.mutate(questionnaire.id);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="py-6 text-center text-muted-foreground"
                    >
                      No submissions yet.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </PageSection>
  );
}
